import api from "./api";

/**
 * Simple email validation regex
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validate if a string is a valid email or a template variable
 * @param {string} value - Value to validate
 * @returns {boolean}
 */
const isValidEmailOrVariable = (value) => {
  if (!value || typeof value !== "string") return false;
  const trimmed = value.trim();
  // Allow template variables like {{Email}}
  if (trimmed.startsWith("{{") && trimmed.endsWith("}}")) return true;
  // Otherwise must be a valid email
  return EMAIL_REGEX.test(trimmed);
};

/**
 * Parse comma-separated emails and filter invalid ones
 * @param {string} value - Comma-separated email list
 * @returns {string} Cleaned comma-separated list or empty string
 */
const sanitizeEmailList = (value) => {
  if (!value || typeof value !== "string") return "";
  
  const emails = value.split(",").map((e) => e.trim()).filter(Boolean);
  const validEmails = emails.filter((email) => isValidEmailOrVariable(email));
  
  return validEmails.join(",");
};

/**
 * Sanitize email payload before sending
 * Removes invalid cc/bcc entries, validates from email
 * @param {Array} payload - Email payload array
 * @returns {Array} Sanitized payload
 */
export const sanitizeEmailPayload = (payload) => {
  return payload.map((item) => {
    switch (item.field) {
      case "cc":
      case "bcc":
        // Filter out invalid emails from cc/bcc
        return { ...item, value: sanitizeEmailList(item.value) };
      case "from":
        // Validate from email - if invalid, return empty
        const fromValue = (item.value || "").trim();
        if (fromValue && !isValidEmailOrVariable(fromValue)) {
          console.warn(`Invalid 'from' email: ${fromValue}`);
          return { ...item, value: "" };
        }
        return item;
      default:
        return item;
    }
  });
};

/**
 * Send email blast to multiple contacts
 * @param {Object} params
 * @param {string} params.tenantId - Tenant/customer ID
 * @param {Object} params.emailSetup - Email configuration
 * @param {Array} params.emailSetup.payload - Email fields (from, to, subject, body, etc.)
 * @param {Array} params.emailSetup.binding - Variable bindings for template
 * @param {Array} params.contacts - Array of contact objects
 * @returns {Promise<{email_sent: string[], email_failed: string[], request_id: string}>}
 */
export const sendEmailBlast = async ({ tenantId, emailSetup, contacts }) => {
  try {
    // Sanitize the payload before sending
    const sanitizedPayload = sanitizeEmailPayload(emailSetup.payload);
    
    // Check for required fields
    const fromField = sanitizedPayload.find((p) => p.field === "from");
    if (!fromField?.value) {
      throw new Error("A valid 'From' email address is required");
    }

    const response = await api.post("/v1/email/send", {
      tenant_id: tenantId,
      email_setup: {
        ...emailSetup,
        payload: sanitizedPayload,
      },
      contacts: contacts,
    });
    return response.data;
  } catch (error) {
    console.error("Error sending email blast:", error);
    throw error;
  }
};

/**
 * Build email payload from form data
 * @param {Object} form - Form values from template editor
 * @returns {Array} Payload array for API
 */
export const buildEmailPayload = (form) => [
  { field: "from", value: form.from || "" },
  { field: "sender_name", value: form.senderName || "" },
  { field: "to", value: form.to || "{{To}}" },
  { field: "subject", value: form.subject || "" },
  { field: "cc", value: form.cc || "" },
  { field: "bcc", value: form.bcc || "" },
  { field: "body", value: form.body || "" },
];

/**
 * Build email bindings from template variables
 * @param {Array} variables - Variables from template editor
 * @returns {Array} Binding array for API
 */
export const buildEmailBindings = (variables = []) => {
  const bindings = [];

  // Always include To binding for email recipient
  bindings.push({
    name: "To",
    variable: "{{To}}",
    origin: "EVENT",
    value: "email",
  });

  // Add bindings for each variable
  variables.forEach((variable) => {
    if (variable.name === "To") return; // Skip, already added

    bindings.push({
      name: variable.name,
      variable: `{{${variable.variable}}}`,
      origin: variable.origin, // "EVENT" or "FIXED"
      value: variable.origin === "EVENT" ? variable.field : variable.value,
    });
  });

  return bindings;
};

/**
 * Normalize contact for email API format
 * Handles both CSV-mapped contacts and database contacts
 * @param {Object} contact - Raw contact object
 * @param {string} source - "csv" or "database"
 * @param {Object} fieldMappings - Field mappings (for CSV source)
 * @returns {Object} Normalized contact for API
 */
export const normalizeContactForEmail = (contact, source = "csv", fieldMappings = {}) => {
  if (source === "csv") {
    // CSV contacts use the field mappings
    return {
      first_name: contact[fieldMappings.first_name] || contact.first_name || "",
      last_name: contact[fieldMappings.last_name] || contact.last_name || "",
      email: contact[fieldMappings.email] || contact.email || "",
      phone: contact[fieldMappings.phone] || contact.phone || "",
      company_name: contact[fieldMappings.company_name] || contact.company_name || "",
      address: contact[fieldMappings.address] || contact.address || "",
      city: contact[fieldMappings.city] || contact.city || "",
      state: contact[fieldMappings.state] || contact.state || "",
      postal_code: contact[fieldMappings.postal_code] || contact.postal_code || "",
    };
  } else {
    // Database contacts - normalize from API response format
    const nameParts = (contact.legalName || "").split(" ");
    return {
      first_name: nameParts[0] || "",
      last_name: nameParts.slice(1).join(" ") || "",
      email: contact.email || "",
      phone: contact.phone || "",
      company_name: contact.legalName || "",
      address: contact.address?.addressLine || "",
      city: contact.address?.addressCity || "",
      state: contact.address?.addressState || "",
      postal_code: contact.address?.addressPostalCode || "",
    };
  }
};
